import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Input, Label } from '@/components/ui';
import { Combobox } from '@/components/ui/combobox';
import { cn } from '@/lib/utils';
import type { PaymentLinkFormatDefinition, LinkParam } from './format-definition';
import type { PayStageEntry } from './stage-map';
import { buildPreviewSegments, buildFullUrl, isCopyableLink } from './link-url';
import { computeHmacSha256Hex } from './hmac-engine';
import { canComputeWithSalt } from './salt';
import { CopyAndQr } from './copy-and-qr';
import { CalculationDetails } from './calculation-details';
import { SnippetPanel } from './snippet-panel';
import { BodyReachBadge } from './body-reach-badge';
import {
  UTM_KEYS,
  checkMetaBudget,
  findParamIssues,
  hasParamIssues,
  missingRequired,
  issueForRow,
  type FreeParamRow,
} from './param-model';
import { useTesterServices, type TesterServiceOption } from './use-tester-services';
// Spec 516: Link-Base je Service (publicBaseUrl) — ein Aufloesungspunkt (D-7)
// + Standalone-Join via Service-Detail (D-8).
import { resolveLinkBase } from './alias-hosts';
import { useService } from '@/hooks/use-services';

/**
 * Payment-Link Link-Tester — Modus A „Link bauen" (Spec 501, Phase 5).
 *
 * Teaser-sicher: ohne geladenes Format bleiben die System-Parameter „Format offen"
 * (deren Keys sind format-gated), die Prefill-Werte erscheinen nur als Info-Badges,
 * und die Signatur der Vorschau ist ein inerter Platzhalter. Frei-Parameter-Tabelle
 * und Kollisions-/Duplikat-Pruefung laufen format-unabhaengig.
 */
export interface BuildTabProps {
  format: PaymentLinkFormatDefinition;
  baseEntry: PayStageEntry;
  salt: string;
  initialServiceId?: string;
  /** Erhöht sich bei jedem Demo-Klick → wendet `format.testVector` an (Spec 503). */
  demoNonce?: number;
  /** Spec 516 D-8: Wizard-Pfad — Service kommt fertig geladen, kein Zweit-Fetch. */
  presetService?: {
    serviceId: string;
    merchantId: string;
    publicBaseUrl?: string;
  };
}

function formatAmount(cents: number | undefined, locale: string): string | null {
  if (cents === undefined) {
    return null;
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

export function BuildTab({
  format,
  baseEntry,
  salt,
  initialServiceId,
  demoNonce = 0,
  presetService,
}: BuildTabProps) {
  const { t, i18n } = useTranslation('payment-link');
  const { options, isLoading, isError } = useTesterServices();
  const [serviceId, setServiceId] = useState(initialServiceId ?? '');
  const [cbOk, setCbOk] = useState('');
  const [freeParams, setFreeParams] = useState<FreeParamRow[]>([]);
  const [sigState, setSigState] = useState<{
    value: string;
    key: string;
  } | null>(null);
  // Demo-injizierte System-Parameter (svc/m/c aus dem Testvektor) — überschreiben
  // den Service-Prefill, solange keine echte Service-Auswahl getroffen wird. `null`
  // = kein Demo-Modus (Standard: Service-Prefill).
  const [demoSystem, setDemoSystem] = useState<LinkParam[] | null>(null);
  const rowCounter = useRef(0);

  // Demo-Auslöser (Spec 503, Phase 7): den offiziellen Testvektor anwenden.
  // `svc`/`m`/`c` gehen als demo-System-Parameter, `cbOk` in sein Feld, der Rest
  // (idR leer) in freie Zeilen. So reproduziert die Demo die exakte Handover-URL,
  // ohne einen echten Service im Picker zu benötigen.
  useEffect(() => {
    if (!demoNonce || !format.testVector) {
      return;
    }
    const tv = format.testVector;
    const cbOkParam = tv.systemParams.find((p) => p.key === 'cbOk');
    setCbOk(cbOkParam?.value ?? '');
    setDemoSystem(tv.systemParams.filter((p) => p.key !== 'cbOk'));
    setServiceId('');
    setFreeParams(
      tv.freeParams.map((p, i) => ({ id: `demo-${i}`, key: p.key, value: p.value })),
    );
    // Nur bei Nonce-Änderung (Demo-Klick).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoNonce]);

  const selected: TesterServiceOption | undefined = options.find(
    (o) => o.serviceId === serviceId,
  );

  // Spec 516 D-8: publicBaseUrl kommt aus der SERVICE-Ressource, nie aus der
  // Provisioning-Aggregat-Zeile (die kennt das Feld nicht, Trap 5).
  // Wizard-Pfad: Preset deckt den vorgewaehlten Service ab -> KEIN Zweit-Fetch.
  // Standalone (oder im Wizard umgewaehlter Service): Detail-Join via
  // useService(serviceId, merchantId) mit der merchantId der Aggregat-Zeile als
  // Routing-Hint (leere serviceId disabled die Query).
  const presetActive =
    !!presetService && serviceId === presetService.serviceId;
  const detailQuery = useService(
    presetActive || !serviceId ? '' : serviceId,
    selected?.merchantId,
  );
  const publicBaseUrl = presetActive
    ? presetService.publicBaseUrl
    : detailQuery.data?.publicBaseUrl;

  // Spec 516 D-7: EIN Aufloesungspunkt — Vorschau, Copy/QR und Snippets
  // konsumieren ausschliesslich dieses Entry.
  const effectiveEntry = resolveLinkBase(publicBaseUrl, baseEntry);

  const comboOptions = useMemo(
    () =>
      options.map((o) => ({
        value: o.serviceId,
        label: o.displayName ? `${o.displayName}` : o.serviceId,
        description: o.merchantId,
      })),
    [options],
  );

  // System-Parameter aus dem Service-Prefill — die Zuordnung ist format-gated,
  // daher via `format.buildSystemParams`. Im Teaser (nicht available) leer →
  // „Format offen". Never-throw: eine fehlerhafte Format-Definition bricht die
  // UI nicht.
  const systemParams: LinkParam[] = useMemo(() => {
    // Demo-Modus: die Testvektor-System-Parameter haben Vorrang.
    if (demoSystem) {
      return demoSystem;
    }
    if (!format.available || !selected) {
      return [];
    }
    try {
      return format.buildSystemParams({
        serviceId: selected.serviceId,
        merchantId: selected.merchantId,
        grossAmountCents: selected.grossAmountCents,
        interval: selected.interval,
        displayName: selected.displayName,
      });
    } catch {
      return [];
    }
  }, [demoSystem, format, selected]);

  // Echte Service-Auswahl beendet den Demo-Modus.
  function selectService(value: string) {
    setServiceId(value);
    setDemoSystem(null);
  }

  const metaBudget = checkMetaBudget(freeParams);

  // `cbOk` (Erfolgs-Rückkehr-URL) ist Pflicht, kommt aber vom Nutzer (nicht aus
  // dem Prefill). Es wird DIREKT NACH `svc` einsortiert — so trifft der gebaute
  // Link die Handover-Beispiel-Reihenfolge `svc, cbOk, m, c, sig` exakt (die
  // Signatur ist byte-sortiert und ordnungs-unabhängig, aber die Demo soll die
  // Handover-URL 1:1 reproduzieren).
  const inputs = useMemo(() => {
    const sys = [...systemParams];
    if (cbOk.trim() !== '') {
      const svcIdx = sys.findIndex((p) => p.key === 'svc');
      sys.splice(svcIdx + 1, 0, { key: 'cbOk', value: cbOk.trim() });
    }
    return {
      systemParams: sys,
      freeParams: freeParams
        .filter((r) => r.key.trim() !== '')
        .map((r) => ({ key: r.key.trim(), value: r.value })),
    };
  }, [systemParams, cbOk, freeParams]);

  // Kollisions-/Case-/`meta_`-Prüfung gegen die TATSÄCHLICHEN System-Keys — inkl.
  // dem feld-verwalteten `cbOk` (aus `inputs.systemParams`, NACH der Insertion).
  // Sonst wäre eine freie `cbOk`-Zeile + gefülltes Pflichtfeld eine kopierbare
  // Duplikat-URL, die der Dienst ablehnt (Integrations-Review P2).
  const issues = findParamIssues(
    inputs.systemParams.map((p) => p.key),
    format.signatureParamName,
    freeParams,
  );

  // Fehlende Pflicht-Parameter (Spec 503, Phase 5): sperren den scharfen Bau/Copy.
  const missing = format.available
    ? missingRequired(
        [
          ...inputs.systemParams.map((p) => p.key),
          ...inputs.freeParams.map((p) => p.key),
        ],
        format.requiredParams,
      )
    : [];

  const blockingParamIssue =
    hasParamIssues(issues) ||
    metaBudget.tooManyKeys ||
    metaBudget.budgetExceeded;

  // Nur mit gültigem Salt (leer = Blocker), ohne blockierende Parameter-Fehler und
  // mit allen Pflicht-Parametern wird scharf signiert.
  const canSign =
    format.available &&
    canComputeWithSalt(salt) &&
    !blockingParamIssue &&
    missing.length === 0;

  // Die Signatur wird an ihren Eingabe-Schlüssel (Salt + Parameter) gebunden. So
  // gilt sie NUR für exakt diese Eingaben — ändern sich Parameter/Salt, ist der
  // abgeleitete `signature`-Wert SOFORT `undefined` (kein veralteter `sig` in
  // Vorschau/Copy), bis der Effekt neu gerechnet hat (Codex-Fund PR-Review).
  const sigKey = `${salt} ${JSON.stringify(inputs)}`;
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!canSign) {
        setSigState(null);
        return;
      }
      try {
        // Never-throw: eine fehlerhafte Format-Definition darf keine unhandled
        // rejection auslösen; der Salt fließt NUR in HMAC, nie in Logs/Fehler.
        const sig = await computeHmacSha256Hex(
          salt,
          format.buildStringToSign(inputs),
        );
        if (!cancelled) {
          setSigState({ value: sig, key: sigKey });
        }
      } catch {
        if (!cancelled) {
          setSigState(null);
        }
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
    // inputs ist über sigKey serialisiert; salt/canSign/format decken den Rest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salt, canSign, sigKey, format]);

  // Nur gültig, wenn die berechnete Signatur zu den AKTUELLEN Eingaben passt.
  const signature =
    sigState && sigState.key === sigKey ? sigState.value : undefined;

  const preview = buildPreviewSegments(format, effectiveEntry, inputs, signature);
  // „So haben wir gerechnet" (Modus A): nur mit berechneter Signatur.
  const stringToSign =
    format.available && signature ? format.buildStringToSign(inputs) : '';

  function addRow(key = '', value = '') {
    rowCounter.current += 1;
    setFreeParams((rows) => [
      ...rows,
      { id: `row-${rowCounter.current}`, key, value },
    ]);
  }

  function updateRow(id: string, patch: Partial<FreeParamRow>) {
    setFreeParams((rows) =>
      rows.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    );
  }

  function removeRow(id: string) {
    setFreeParams((rows) => rows.filter((r) => r.id !== id));
  }

  const amount = formatAmount(selected?.grossAmountCents, i18n.language);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Linke Spalte: Eingaben */}
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="tester-service">{t('tester.build.service.label')}</Label>
          <Combobox
            id="tester-service"
            options={comboOptions}
            value={serviceId}
            onChange={selectService}
            placeholder={t('tester.build.service.placeholder')}
            searchPlaceholder={t('tester.build.service.search')}
            emptyText={
              isError
                ? t('tester.build.service.error')
                : t('tester.build.service.empty')
            }
            loading={isLoading}
            error={isError}
          />
        </div>

        {/* Prefill nur als Orientierung (Info-Badges, keine Eingabe). */}
        {selected && (
          <div className="flex flex-wrap gap-2" data-testid="prefill-badges">
            {amount && (
              <Badge variant="secondary">
                {t('tester.build.prefill.amount')}: {amount}
              </Badge>
            )}
            {selected.interval && (
              <Badge variant="secondary">
                {t('tester.build.prefill.interval')}:{' '}
                {t(`services:intervalShort.${selected.interval}`, {
                  defaultValue: selected.interval,
                })}
              </Badge>
            )}
            <Badge variant="secondary">
              {t('tester.build.prefill.idempotency')}: {selected.idempotencyMode}
            </Badge>
            <Badge variant={selected.hasClickWebhook ? 'default' : 'outline'}>
              {selected.hasClickWebhook
                ? t('tester.build.prefill.webhookOn')
                : t('tester.build.prefill.webhookOff')}
            </Badge>
          </div>
        )}

        {/* System-Parameter: gesperrt, vorbefuellt, Badge signiert/frei. Deren
            Keys sind format-gated → im Teaser „Format offen". */}
        <div className="space-y-2">
          <Label>{t('tester.build.params.systemTitle')}</Label>
          {!format.available ? (
            <p className="text-xs text-muted-foreground">
              {t('tester.build.params.unknownBadge')}
            </p>
          ) : systemParams.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {t('tester.build.params.systemEmpty')}
            </p>
          ) : (
            <div className="space-y-2" data-testid="system-params">
              {systemParams.map((p) => (
                <div key={p.key} className="flex items-center gap-2">
                  <Input
                    readOnly
                    aria-label={p.key}
                    value={`${p.key}=${p.value}`}
                    className="bg-muted/50 font-mono text-xs"
                  />
                  <BodyReachBadge format={format} paramKey={p.key} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* cbOk (Erfolgs-Rückkehr-URL) — Pflicht, nutzergesetzt (Spec 503). */}
        {format.available && (
          <div className="space-y-1.5">
            <Label htmlFor="tester-cbok">{t('tester.build.cbOk.label')}</Label>
            <Input
              id="tester-cbok"
              value={cbOk}
              onChange={(e) => setCbOk(e.target.value)}
              placeholder={t('tester.build.cbOk.placeholder')}
              className="font-mono text-xs"
              aria-label="cbOk"
            />
          </div>
        )}

        {/* Freie Parameter (format-unabhaengig). */}
        <div className="space-y-2">
          <Label>{t('tester.build.params.freeTitle')}</Label>

          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>{t('tester.build.params.utmHint')}</span>
            {UTM_KEYS.map((utm) => (
              <button
                key={utm}
                type="button"
                onClick={() => addRow(utm)}
                className="touch-manipulation rounded-full border px-2 py-0.5 hover:bg-accent coarse:min-h-10 coarse:px-3"
              >
                {utm}
              </button>
            ))}
          </div>
          {/* Rohe utm_* sind wirkungslos (mitsigniert, vom Body verworfen) — nur
              meta_-Parameter erreichen die Facade (Spec 503). */}
          <p className="text-xs text-muted-foreground">
            {t('tester.build.params.utmExplain')}
          </p>

          <div className="space-y-2">
            {freeParams.map((row) => {
              const issue = issueForRow(issues, row.id);
              // `caseMismatchWithSystem` ist ein WEICHER Hinweis (blockiert nicht) —
              // kein destruktives Styling, und der Body-Reach-Badge bleibt sichtbar.
              const softIssue = issue === 'caseMismatchWithSystem';
              const blockingIssue = issue !== undefined && !softIssue;
              return (
                <div key={row.id} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Input
                      aria-label={t('tester.build.params.keyPlaceholder')}
                      placeholder={t('tester.build.params.keyPlaceholder')}
                      value={row.key}
                      onChange={(e) => updateRow(row.id, { key: e.target.value })}
                      className={cn(blockingIssue && 'border-destructive')}
                    />
                    <Input
                      aria-label={t('tester.build.params.valuePlaceholder')}
                      placeholder={t('tester.build.params.valuePlaceholder')}
                      value={row.value}
                      onChange={(e) =>
                        updateRow(row.id, { value: e.target.value })
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('tester.build.params.remove')}
                      onClick={() => removeRow(row.id)}
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                  {/* Body-Reach-Badge bleibt bei WEICHEN Hinweisen sichtbar — jeder
                      Nicht-sig-Parameter zeigt seine Klassifikation (Spec 503). */}
                  {row.key.trim() !== '' && !blockingIssue && (
                    <BodyReachBadge format={format} paramKey={row.key.trim()} />
                  )}
                  {issue && (
                    <p
                      className={cn(
                        'text-xs',
                        softIssue ? 'text-amber-700' : 'text-destructive',
                      )}
                    >
                      {t(`tester.build.params.issue.${issue}`)}
                    </p>
                  )}
                </div>
              );
            })}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addRow()}
            >
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
              {t('tester.build.params.add')}
            </Button>

            {/* meta_-Budget (Handover §5): max. 10 Keys, ≤ 4096 Byte. */}
            {metaBudget.tooManyKeys && (
              <p className="text-xs text-destructive" data-testid="meta-too-many">
                {t('tester.build.params.metaTooManyKeys')}
              </p>
            )}
            {metaBudget.budgetExceeded && (
              <p className="text-xs text-destructive" data-testid="meta-budget">
                {t('tester.build.params.metaBudgetExceeded')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Rechte Spalte: Live-Vorschau (aria-live). */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label>{t('tester.build.preview.title')}</Label>
          {/* Spec 516: Herkunft der Link-Base sichtbar machen (Alias vs. Default). */}
          <Badge
            variant={effectiveEntry.source === 'service-alias' ? 'default' : 'secondary'}
            data-testid="base-source"
          >
            {effectiveEntry.source === 'service-alias'
              ? t('tester.build.base.serviceAlias')
              : t('tester.build.base.stageDefault')}
          </Badge>
        </div>
        <div
          aria-live="polite"
          data-testid="url-preview"
          className="break-all rounded-md border bg-muted/30 p-3 font-mono text-sm"
        >
          <span
            className={cn(
              'text-muted-foreground',
              preview.basePlaceholder && 'line-through decoration-amber-500',
            )}
          >
            {preview.base}
          </span>
          {/* Body-Reach-Segmente (Spec 503): System/Routing/meta_ erreichen den
              Body bzw. steuern die Verarbeitung (Akzent); rohe Parameter sind
              mitsigniert, aber wirkungslos (gedämpft); unknown = Teaser. */}
          {[...preview.system, ...preview.routing, ...preview.meta].map((p, i) => (
            <span key={`b-${i}-${p.key}`} className="text-sky-600">
              {' '}
              {p.key}={p.value}
            </span>
          ))}
          {preview.raw.map((p, i) => (
            <span key={`r-${i}-${p.key}`} className="text-muted-foreground">
              {' '}
              {p.key}={p.value}
            </span>
          ))}
          {preview.unknown.map((p, i) => (
            <span key={`u-${i}-${p.key}`} className="text-foreground">
              {' '}
              {p.key}={p.value}
            </span>
          ))}
          <span className="text-emerald-600">
            {' '}
            {preview.signature.paramName
              ? `${preview.signature.paramName}=${preview.signature.inert ? '…' : preview.signature.value}`
              : /* Teaser: KEIN geratener Parameter-Name (Owner-Leitplanke) — neutrales Label. */
                `⟨${t('tester.build.preview.signatureLabel')}⟩`}
          </span>
        </div>

        <p className="text-xs text-muted-foreground">
          {t('tester.build.preview.legend')}
        </p>

        {/* Pflichtfeld-Hinweis: fehlt cbOk o. a., wird nicht scharf signiert. */}
        {format.available && missing.length > 0 && (
          <p className="text-xs text-amber-700" data-testid="missing-required">
            {t('tester.build.params.missingRequired', {
              params: missing.join(', '),
            })}
          </p>
        )}

        {preview.signature.inert && missing.length === 0 && (
          <p className="text-xs text-muted-foreground">
            {t('tester.build.preview.signaturePending')}
          </p>
        )}

        {preview.basePlaceholder && (
          <p className="flex items-center gap-1 text-xs text-amber-700">
            <Lock className="h-3 w-3" aria-hidden="true" />
            {t('tester.build.preview.prodPlaceholder')}
          </p>
        )}

        {/* Fertiger, signierter Link: Copy + QR (Prod-Platzhalter bleibt gesperrt). */}
        <CopyAndQr
          url={buildFullUrl(
            effectiveEntry,
            [...inputs.systemParams, ...inputs.freeParams],
            format.signatureParamName,
            signature ?? '',
            format.signaturePlacement,
            format.clickPath ?? '',
          )}
          enabled={isCopyableLink(format, effectiveEntry, signature)}
        />

        {/* „So haben wir gerechnet" — nur mit berechneter Signatur. */}
        {signature && (
          <CalculationDetails
            stringToSign={stringToSign}
            expected={signature}
          />
        )}

        <SnippetPanel
          ctx={{
            baseUrl: `${effectiveEntry.payBaseUrl}${format.clickPath ?? ''}`,
            params: [...inputs.systemParams, ...inputs.freeParams],
            signatureParamName: format.signatureParamName,
          }}
        />
      </div>
    </div>
  );
}
